import { extractGraphQLErrorDetailsToDisplay } from '@cosuno/apollo';
import { showToast, toast } from '@cosuno/cosuno-ui';
import i18n from 'i18next';

import { ERROR_TYPES } from '~/packages/constants';

interface ErrorOptions {
  title?: string;
  message: string;
}

/**
 * Can be used to define which title and message should be shown
 * by throwing an error, without showing an additional toast,
 * in places where a caught exception is already passed to `toast.error`.
 *
 * This allows for more flexibility during implementation but
 * is not the default way to show an error toast. See {@link error} instead.
 */
export class ToastError extends Error {
  readonly extensions: { errorToast: ErrorOptions };

  constructor(errorToast: ErrorOptions) {
    super();

    this.extensions = { errorToast };
  }
}

const errorBase = ({ title, message }: ErrorOptions): void => {
  const errorTitle = title || i18n.t('error');

  toast.error(errorTitle, message);
};

const errorGeneric = (): void => {
  toast.error(i18n.t('genericError.title'), i18n.t('genericError.message'));
};

const handleToastWithError = (error?: Error | ErrorOptions): void => {
  // handle generic error by just throwing it
  if (!error) {
    errorGeneric();
    return;
  }

  // handle ErrorOptions
  if (!(error instanceof Error)) {
    errorBase(error);
    return;
  }

  // handle ToastError
  if (error instanceof ToastError) {
    errorBase(error.extensions.errorToast);
    return;
  }

  // handle apollo errors
  const apolloErrorDescription = extractGraphQLErrorDetailsToDisplay(error, {
    [ERROR_TYPES.docuSignEnvelopeDoesNotExistError]: {
      title: i18n.t('projectBidPackages:contract.docuSignEnvelopeExpiredWarning.title'),
      message: i18n.t('projectBidPackages:contract.docuSignEnvelopeExpiredWarning.body'),
      severity: 'warning',
    },
  });
  if (apolloErrorDescription) {
    const { message, title = i18n.t('error'), severity = 'danger' } = apolloErrorDescription;

    showToast({ message, title, type: severity });
    return;
  }

  // otherwise default to generic error
  errorGeneric();
};

export default {
  error: (
    ...[titleOrError, message]: Parameters<typeof toast.error | typeof handleToastWithError>
  ) =>
    typeof titleOrError === 'string'
      ? toast.error(titleOrError, message)
      : handleToastWithError(titleOrError),
  success: toast.success,
  info: toast.info,
  warning: toast.warning,
};
